import { useState, useEffect, useRef } from "react";
import type { TooltipPosition, TooltipState } from "./Tooltip.types";

type OriginalTitleRef = {
  target: HTMLElement;
  title: string;
} | null;

export const useTooltip = () => {
  const [tooltipState, setTooltipState] = useState<TooltipState>({
    isVisible: false,
    content: null,
    position: "top",
    coords: null,
  });
  const originalTitleRef = useRef<OriginalTitleRef>(null);

  useEffect(() => {
    const handleMouseOver = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const tooltipTarget = target.closest("[title]") as HTMLElement | null;

      // Se não houver alvo com 'title' ou o 'title' estiver vazio, esconde e sai
      if (!tooltipTarget || !tooltipTarget.getAttribute("title")) {
        handleMouseOut();
        return;
      }

      // Se já estamos no mesmo alvo, não faz nada
      if (tooltipTarget === originalTitleRef.current?.target) {
        return;
      }

      // Limpa o tooltip anterior (se houver)
      handleMouseOut();

      const title = tooltipTarget.getAttribute("title")!;
      const position = (tooltipTarget.getAttribute("data-position") || "top") as TooltipPosition;

      // Salva o 'title' original e o remove para evitar o tooltip nativo
      originalTitleRef.current = { target: tooltipTarget, title };
      tooltipTarget.removeAttribute("title");

      setTooltipState((prev) => ({
        ...prev,
        isVisible: true,
        content: title,
        position: position,
      }));
    };

    const handleMouseOut = () => {
      // Restaura o 'title' nativo ao elemento
      if (originalTitleRef.current) {
        originalTitleRef.current.target.setAttribute("title", originalTitleRef.current.title);
        originalTitleRef.current = null;
      }

      // Esconde o tooltip
      setTooltipState((prev) => ({ ...prev, isVisible: false, content: null }));
    };

    const handleMouseMove = (event: MouseEvent) => {
      // Atualiza as coordenadas do cursor para o tooltip seguir
      setTooltipState((prev) => ({
        ...prev,
        coords: { top: event.clientY, left: event.clientX },
      }));
    };

    // Anexa os ouvintes de evento globais
    document.body.addEventListener("mouseover", handleMouseOver);
    document.body.addEventListener("mouseout", handleMouseOut);
    document.body.addEventListener("mousemove", handleMouseMove);

    return () => {
      // Limpeza: remove os ouvintes
      document.body.removeEventListener("mouseover", handleMouseOver);
      document.body.removeEventListener("mouseout", handleMouseOut);
      document.body.removeEventListener("mousemove", handleMouseMove);

      // Garante que o 'title' seja restaurado se o provider for desmontado
      if (originalTitleRef.current) {
        originalTitleRef.current.target.setAttribute("title", originalTitleRef.current.title);
      }
    };
  }, []); // O array vazio garante que isso rode apenas na montagem e desmontagem

  return tooltipState;
};
